import { Button, H6, Icon } from "@alternative/components";
import { IconNames } from "@alternative/icons";
import { Collapse, Popover } from "@blueprintjs/core";
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { acceptedExtensions, duplicateEntryTypes } from "../../constants";
import { apiKeyState, designListState, toastState } from "../../recoil/filemanager/atoms";
import { fetchInterfaceElements } from "../../recoil/filemanager/selectors";
import {
  convertFeetInchToNumber,
  deleteFolders,
  filterFileArrFromExt,
  findDuplicateFiles,
  uploadFiles,
  uploadFolder,
} from "../../utils";
import { DuplicateEntryDialog, UploadErrorDialog } from "./AlertDialogs";
import Dialog from "./Dialog";
import InputButton from "./InputButton";
import { DisplayUnit } from "./PreviewSection";
import convert from "convert-units";
const CARET = "at-upload-filelist-caret";
const CARET_OPEN = "at-upload-filelist-caret--expanded";
const CAERT_CLOSED = "at-upload-filelist-caret--collapsed";
const defDims = { physicalWidth: null, physicalHeight: null, unit: "ft" };
const UploadFileNode = (props) => {
  const { file, dims, handleChangeWidth, handleChangeHeight, handleChangeUnit } = props;
  const [isOpen, setIsOpen] = useState(false);
  return (
    <li className="at-upload-filelist-item">
      <div className="at-upload-filelist-item__header">
        <H6>{file.name}</H6>
        <Icon
          className={`${CARET} ${isOpen ? CARET_OPEN : CAERT_CLOSED}`}
          onClick={() => setIsOpen(!isOpen)}
          icon={IconNames.CHEVRON_DOWN}
        />
      </div>
      <Collapse isOpen={isOpen}>
        <DisplayUnit
          physicalHeight={dims.physicalHeight}
          physicalWidth={dims.physicalWidth}
          unit={dims.unit}
          handleChangeHeight={handleChangeHeight}
          handleChangeWidth={handleChangeWidth}
          handleChangeUnit={handleChangeUnit}
        />
      </Collapse>
    </li>
  );
};
const UploadButton = (props) => {
  const [designList, setDesignList] = useRecoilState(designListState);
  const { designTree, selectedFolders } = designList;

  const apiKey = useRecoilValue(apiKeyState);
  const [toastListState, setToastLisState] = useRecoilState(toastState);
  const { uploadStatusToast } = toastListState;
  const interfaceElements = useRecoilValue(fetchInterfaceElements);
  const forceUnit =
    interfaceElements.flags.fileManager && interfaceElements.flags.fileManager.forceUnit
      ? interfaceElements.flags.fileManager.forceUnit
      : "";
  const [dialog, setDialog] = useState({
    isOpen: false,
    duplicateItems: [],
    allFiles: [],
    entryType: duplicateEntryTypes.NONE,
    allDims: null,
  });
  const [sizeInfoDialog, setSizeInfoDialog] = useState({
    isOpen: false,
    files: [],
    entryType: duplicateEntryTypes.NONE,
  });
  const [errDialog, setErrDialog] = useState({
    isOpen: false,
    items: [],
    status: null,
  });
  const handleFileUpload = async (fileArray, dims) => {
    if (!fileArray || !fileArray.length) return;
    const filesToUpload = fileArray.map((item) => item.name);
    let newUploadStatusToast = { ...uploadStatusToast, files: filesToUpload.length, progress: 0, canDismiss: false };
    setToastLisState({ ...toastListState, uploadStatusToast: newUploadStatusToast });
    const updatedDesignList = await uploadFiles({
      key: apiKey,
      fileArray,
      dims,
      forceUnit,
      designTree,
      parentNode: selectedFolders[0],
      onFileUpload: (filesUploaded) => {
        newUploadStatusToast = {
          ...newUploadStatusToast,
          progress: ((filesUploaded.length / filesToUpload.length) * 100).toFixed(1),
        };
        setToastLisState({ ...toastListState, uploadStatusToast: newUploadStatusToast });
      },
    });

    setToastLisState({
      ...toastListState,
      uploadStatusToast: { ...newUploadStatusToast, canDismiss: true, progress: 100 },
    });
    setDesignList({ ...designList, ...updatedDesignList });
  };
  const uploadFileIfNeeded = (fileArray) => {
    if (interfaceElements.isJpeg) {
      const files = fileArray.map((file) => ({
        file,
        dims: defDims,
      }));
      setSizeInfoDialog({ isOpen: true, files, entryType: duplicateEntryTypes.FILE });
    } else handleFileUpload(fileArray);
  };
  const handleFolderUpload = async (fileArray, operation, dims) => {
    console.log("handleFolderUpload -> dims", dims);
    const filesToUpload = fileArray.map((item) => item.name);

    const parentNode = selectedFolders[0];
    let newUploadStatusToast = { ...uploadStatusToast, files: filesToUpload.length, progress: 0 };

    setToastLisState({ ...toastListState, uploadStatusToast: newUploadStatusToast });
    let updatedTree = designTree;
    if (operation === "replace") {
      const updatedList = await deleteFolders({ apiKey, designTree, folders: dialog.duplicateItems });
      updatedTree = updatedList.updatedTree;
    }

    const updatedDesignList = await uploadFolder({
      key: apiKey,
      fileArray,
      designTree: updatedTree,
      dims,
      forceUnit,
      parentNode,
      operation,
      onFileUpload: (filesUploaded) => {
        newUploadStatusToast = {
          ...newUploadStatusToast,
          progress: ((filesUploaded.length / filesToUpload.length) * 100).toFixed(1),
        };
        setToastLisState({ ...toastListState, uploadStatusToast: newUploadStatusToast });
      },
    });
    setToastLisState({
      ...toastListState,
      uploadStatusToast: { ...newUploadStatusToast, canDismiss: true, progress: 100 },
    });
    setDesignList({ ...designList, ...updatedDesignList, selectedDesigns: [] });
  };
  const uploadFolderIfNeeded = (fileArray, operation) => {
    if (interfaceElements.isJpeg) {
      const files = fileArray.map((file) => ({
        file,
        dims: defDims,
      }));
      setSizeInfoDialog({ isOpen: true, files, entryType: duplicateEntryTypes.FOLDER });
    } else handleFolderUpload(fileArray, operation);
  };
  const filterFileExtensions = (files) => {
    //filter extensions
    const acceptedExt = !interfaceElements.isJpeg ? acceptedExtensions.CTF : acceptedExtensions.JPEG;
    return filterFileArrFromExt(Array.from(files), acceptedExt.arr);
  };
  const uploadPopup = (
    <div className="upload-popup">
      <H6>Uploading to...</H6>
      <p className="at-text-small">{selectedFolders[0].fullPath}</p>
      <InputButton
        buttonProps={{ intent: "primary", tertiary: true, text: "Upload Folder" }}
        inputProps={{
          webkitdirectory: "true",
          mozdirectory: "true",
          msdirectory: "true",
          directory: "true",
          multiple: true,
          onInput: (e) => {
            const fileArray = filterFileExtensions(e.target.files);
            const parentNode = selectedFolders[0];
            const uploadFolderName = fileArray[0].webkitRelativePath.split("/")[0];
            const duplicateFolder = parentNode.children.find(
              (item) => item.name.toLowerCase() === uploadFolderName.toLowerCase()
            );
            if (duplicateFolder) {
              setDialog({
                entryType: duplicateEntryTypes.FOLDER,
                isOpen: true,
                duplicateItems: [duplicateFolder],
                allFiles: fileArray,
              });
            } else {
              uploadFolderIfNeeded(fileArray);
            }
          },
        }}
      />
      <InputButton
        buttonProps={{ intent: "primary", tertiary: true, text: "Upload File" }}
        inputProps={{
          accept: !interfaceElements.isJpeg ? acceptedExtensions.CTF.ext : acceptedExtensions.JPEG.ext,
          multiple: true,
          onInput: (e) => {
            //filter extensions
            const acceptedExt = !interfaceElements.isJpeg ? acceptedExtensions.CTF : acceptedExtensions.JPEG;
            const fileArray = filterFileArrFromExt(Array.from(e.target.files), acceptedExt.arr);
            if (!fileArray.length) return;
            //find duplicate files
            const duplicateFiles = findDuplicateFiles({ selectedFolder: selectedFolders[0], fileArray });
            if (duplicateFiles.length) {
              setDialog({
                entryType: duplicateEntryTypes.FILE,
                isOpen: true,
                duplicateItems: duplicateFiles,
                allFiles: fileArray,
              });
              return;
            }
            //show size dialog if jpeg
            uploadFileIfNeeded(fileArray);
          },
        }}
      />
    </div>
  );
  const handleChangeWidth = (width, fileIndex) => {
    const files = [...sizeInfoDialog.files];
    const currentDims = files[fileIndex].dims;

    const physicalWidth = convertFeetInchToNumber(width, currentDims.unit);
    const dims = { ...currentDims, physicalWidth };
    files.splice(fileIndex, 1, { ...files[fileIndex], dims });
    setSizeInfoDialog({ ...sizeInfoDialog, files });
  };

  const handleChangeHeight = (height, fileIndex) => {
    const files = [...sizeInfoDialog.files];
    const currentDims = files[fileIndex].dims;

    const physicalHeight = convertFeetInchToNumber(height, currentDims.unit);
    const dims = { ...currentDims, physicalHeight };
    files.splice(fileIndex, 1, { ...files[fileIndex], dims });
    setSizeInfoDialog({ ...sizeInfoDialog, files });
  };
  const handleChangeUnit = (unit, fileIndex) => {
    const files = [...sizeInfoDialog.files];
    const currentDims = files[fileIndex].dims;
    const physicalWidth = convert(currentDims.physicalWidth)
      .from(currentDims.unit)
      .to(unit);
    const physicalHeight = convert(currentDims.physicalHeight)
      .from(currentDims.unit)
      .to(unit);
    const dims = { physicalWidth, physicalHeight, unit };
    files.splice(fileIndex, 1, { ...files[fileIndex], dims });
    setSizeInfoDialog({ ...sizeInfoDialog, files });
  };
  return (
    <>
      <Popover content={uploadPopup} interactionKind="hover">
        <Button minimal>
          <Icon icon={IconNames.UPLOAD} />
          <H6>Upload</H6>
        </Button>
      </Popover>
      <UploadErrorDialog
        isOpen={errDialog.isOpen}
        status={errDialog.status}
        onClose={() => setDialog({ ...dialog, isOpen: false })}
        items={errDialog.items}
      />
      <DuplicateEntryDialog
        isOpen={dialog.isOpen}
        entryType={dialog.entryType}
        items={dialog.duplicateItems}
        onClose={() => setDialog({ ...dialog, isOpen: false })}
        onRename={() => {
          const newFiles = dialog.allFiles.map((item) => {
            if (dialog.duplicateItems.find((dupFile) => dupFile.filename === item.name)) {
              const lastIndex = item.name.lastIndexOf(".");
              const name = item.name.substr(0, lastIndex);
              const extension = item.name.substr(lastIndex + 1);
              return new File([item], `${name} 1.${extension}`, { type: item.type });
            } else return item;
          });
          if (dialog.entryType === duplicateEntryTypes.FILE) uploadFileIfNeeded(newFiles);
          else uploadFolderIfNeeded(newFiles, "rename");
          setDialog({ isOpen: false });
        }}
        onReplace={() => {
          if (dialog.entryType === duplicateEntryTypes.FILE) uploadFileIfNeeded(dialog.allFiles);
          else uploadFolderIfNeeded(dialog.allFiles, "replace");
          setDialog({ isOpen: false });
        }}
      />
      <Dialog
        isOpen={sizeInfoDialog.isOpen}
        onClose={() => setSizeInfoDialog({ ...sizeInfoDialog, isOpen: false })}
        header="Size information required"
      >
        <p>Before uploading, please set physical sizes for these JPEG files:</p>
        {/* <div>
          <input type="checkbox" />
          Set same size for all designs listed here
        </div> */}
        <div>
          <ul className="at-upload-filelist">
            {sizeInfoDialog.files.map((item, i) => (
              <UploadFileNode
                key={i}
                file={item.file}
                dims={item.dims}
                handleChangeWidth={(width) => handleChangeWidth(width, i)}
                handleChangeHeight={(height) => handleChangeHeight(height, i)}
                handleChangeUnit={(unit) => handleChangeUnit(unit, i)}
              />
            ))}
          </ul>
        </div>
        <div>
          <Button
            text="Upload"
            intent="primary"
            onClick={() => {
              const files = sizeInfoDialog.files.map((item) => item.file);
              const dims = sizeInfoDialog.files.map((item) => item.dims);
              if (sizeInfoDialog.entryType === duplicateEntryTypes.FILE) handleFileUpload(files, dims);
              else handleFolderUpload(files, undefined, dims);
              setSizeInfoDialog({ ...sizeInfoDialog, isOpen: false });
            }}
          />
          <Button
            text="Cancel"
            onClick={() => {
              setSizeInfoDialog({ ...sizeInfoDialog, isOpen: false });
            }}
          />
        </div>
      </Dialog>
    </>
  );
};

export default UploadButton;
